import { City, Program, RouteForVehicles, RouteStop, Slot } from '@/new-transport/_models/transport-map.model';
import { StopKind, TransportTerm, VehicleTransport } from '@/new-transport/_models/transport.model';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';

export class TransportMapValidator {
	private readonly transportName: string;
	private readonly transportRoute: RouteForVehicles;
	private readonly citiesWithPrograms: City[];
	private readonly destinations: TransportTerm['transportStop'][];
	private readonly takenPrograms: Map<number, Program[]>;
	private readonly vehicles: VehicleTransport[];
	private readonly transportKind: OfferTransportKind;

	constructor(
		transportName: string,
		transportRoute: RouteForVehicles,
		citiesWithPrograms: City[],
		destinations: TransportTerm['transportStop'][],
		takenPrograms: Map<number, Program[]>,
		vehicles: VehicleTransport[],
		transportKind: OfferTransportKind,
	) {
		this.transportName = transportName;
		this.transportRoute = transportRoute;
		this.citiesWithPrograms = citiesWithPrograms;
		this.destinations = destinations;
		this.takenPrograms = takenPrograms;
		this.vehicles = vehicles;
		this.transportKind = transportKind;
	}

	validate(): void {
		try {
			this.validateTransportName();
			this.validateHasAtLeastOneStop();
			this.validateAllCitiesWithSaleIncluded();
			this.validateAllDestinationsIncluded();
			this.validateAllParticipantsIncluded();
			this.validateAllVehiclesHasTargetStop();
		} catch (err) {
			throw new Error(err);
		}
	}

	private validateTransportName(): void {
		if (new RegExp('^\\s+$').test(this.transportName) || !this.transportName) {
			throw new Error(`Nazwa transportu nie została podana!`);
		}
	}

	private validateHasAtLeastOneStop(): void {
		if (!this.route.length) {
			throw new Error(`Trasa nie zawiera żadnych przystanków!`);
		}
	}

	private validateAllCitiesWithSaleIncluded(): void {
		const citiesWithPrograms = this.citiesWithPrograms.filter((city) => city.programsFromOfferTransport.length);

		const citiesNotIncluded = citiesWithPrograms.filter(
			(city) => !this.route.some((stop) => stop.cityId === city.id),
		);

		if (citiesNotIncluded.length) {
			const citiesNames = citiesNotIncluded.map((city) => city.location.name).join(', ');
			throw new Error(`Miasta z sprzedażą: ${citiesNames} nie zostały dodane do trasy!`);
		}
	}

	private validateAllDestinationsIncluded(): void {
		const destinations = this.destinations.map((destination) => destination.id);

		destinations.forEach((destination) => {
			const destinationIncluded = this.route.some((stop) => stop.cityId === destination);
			if (!destinationIncluded) {
				throw new Error(`Nie wszystkie destynacje zostały dodane do trasy!`);
			}
		});
	}

	private validateAllParticipantsIncluded(): void {
		const participantsToBeIncluded: Set<number> = new Set(
			this.citiesWithPrograms
				.map((city) => this.getSlotsFromPrograms(city.programsFromOfferTransport))
				.flat()
				.map((v) => v.id),
		);

		const includedParticipants: Set<number> = new Set(
			this.getSlotsFromPrograms(Array.from(this.takenPrograms.values()).flat()).map((v) => v.id),
		);

		const diff = Array.from(participantsToBeIncluded).filter((item) => !includedParticipants.has(item));

		if (diff.length > 0) {
			throw new Error('Nie wszyscy uczestnicy zostali dodani do trasy!');
		}
	}

	private get route(): RouteStop[] {
		return Array.from(this.transportRoute.values()).flat();
	}

	private getSlotsFromPrograms(programs: Program[]): Slot[] {
		return Array.from(programs)
			.map((programs) => Array.from(programs.reservation.values()).flat())
			.flat();
	}

	private validateAllVehiclesHasTargetStop(): void {
		if (this.transportKind !== OfferTransportKind.PLANE) {
			return;
		}

		const invalidKeys = Array.from(this.transportRoute.entries())
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			.filter(([_, arr]) => {
				const lastItem = arr[arr.length - 1];
				return lastItem.action !== StopKind.TARGET;
			})
			.map(([key]) => key);

		const invalidVehiclesNames = invalidKeys
			.map((v) => this.vehicles.find((vehicle) => vehicle.id === v)?.name)
			.filter(Boolean);

		if (invalidVehiclesNames.length) {
			throw new Error(
				`Każdy samolot musi być połączony z destynacją. <br> Niepołączone pojazdy: ${invalidVehiclesNames}`,
			);
		}
	}
}
